import React, { Fragment, PureComponent } from 'react';
import withStyles from '@mui/styles/withStyles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Collapse from '@mui/material/Collapse';
import cx from 'classnames';
import { isNullOrUndefined } from '../../util/objects';
import GatedComponent from '../GatedComponent';
import Tooltip from '../form/Tooltip';
import { navigationItems } from './drawerConstants';

class DrawerListItem extends PureComponent {
    state = {
        expanded: false
    };

    componentDidMount() {
        const { activeItem, item } = this.props;
        if (isActive(item, activeItem)) this.setState({ expanded: true });
        else if (!!item.expanded) this.setState({ expanded: true });
    }

    onClick = e => {
        const { onNav, item } = this.props;

        if (item.path) {
            if (item.newTab) {
                let win = window.open(item.path, '_blank');
                win.focus();
            } else {
                onNav(item.path);
            }
        } else if (item.items) {
            const expanded = !this.state.expanded;
            this.setState({ expanded });
        }
    };

    onExpand = e => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        this.setState({ expanded: !this.state.expanded });
    };

    render() {
        const { item, activeItem, nested, hidden, minimized, classes, renderItem, renderGroup } = this.props;

        let renderFunc;
        if (item.items && renderGroup) renderFunc = renderGroup;
        else if (!item.items && renderItem) renderFunc = renderItem;

        return (
            <GatedComponent key={item.id || item.label} showComponentCode={item.permission}>
                {() => {
                    return (
                        <Fragment>
                            <ListItem
                                button
                                onClick={this.onClick}
                                className={cx(classes.root, {
                                    [classes.active]: isActive(item, activeItem),
                                    [classes.nested]: nested,
                                    [classes.minimized]: minimized
                                })}
                                aria-hidden={hidden}
                                tabIndex={hidden ? -1 : 0}
                            >
                                {item.icon && this.renderIcon()}
                                {renderFunc ? renderFunc(item) : this.renderText()}
                                {this.renderAction()}
                            </ListItem>
                            {this.renderChildItems()}
                        </Fragment>
                    );
                }}
            </GatedComponent>
        );
    }

    renderIcon() {
        const { item, activeItem, classes } = this.props;
        return (
            <Tooltip title={item.label}>
                <ListItemIcon>
                    <item.icon className={cx(classes.icon, { [classes.iconActive]: item === activeItem })} />
                </ListItemIcon>
            </Tooltip>
        );
    }

    renderText() {
        const { item, activeItem, classes } = this.props;
        return (
            <ListItemText
                primary={item.label}
                classes={{
                    primary: cx(classes.text, item.newTab && 'targetNewPage', {
                        [classes.textActive]: item === activeItem
                    })
                }}
            />
        );
    }

    renderAction() {
        const { item, minimized } = this.props;
        if (minimized || !item.items) return;
        const { expanded } = this.state;
        return (
            <IconButton onClick={this.onExpand} size="large">
                <Icon>{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
            </IconButton>
        );
    }

    renderChildItems() {
        const { item, minimized, activeItem } = this.props;
        if (!item.items) return;
        const { expanded } = this.state;
        const activePath = activeItem && activeItem.path ? activeItem.path : '';
        const itemPath = item && item.path ? item.path : '';
        let inPath = activePath.startsWith(itemPath);
        if ('/' === itemPath) {
            const homeItems = navigationItems.find(obj => '/' === obj.path);
            inPath = activePath === itemPath || !!homeItems.items.find(obj => activePath === obj.path);
        }
        return (
            <Collapse in={minimized ? inPath : expanded}>
                <List component="div" disablePadding style={{ zoom: minimized ? 0.75 : null }}>
                    {item.items.map(this.renderChildItem)}
                </List>
            </Collapse>
        );
    }

    renderChildItem = item => {
        const { minimized, activeItem, hidden, renderItem, renderGroup, onNav } = this.props;
        return (
            <GatedComponent key={item.id || item.label} showComponentCode={item.permission}>
                {() => {
                    return (
                        <DrawerListItemConnected
                            key={item.id || item.label}
                            item={item}
                            nested
                            onNav={onNav}
                            minimized={minimized}
                            activeItem={activeItem}
                            hidden={hidden || !this.state.expanded}
                            renderItem={renderItem}
                            renderGroup={renderGroup}
                        />
                    );
                }}
            </GatedComponent>
        );
    };
}

const isActive = (self, activeItem) => {
    if (self === activeItem) return true;

    if (isNullOrUndefined(self.items)) return false;

    return !isNullOrUndefined(self.items.find(x => x === activeItem));
};

const styles = ({ spacing, typography, transitions, palette }) => ({
    root: {
        height: 48,
        paddingRight: 0,
        transition: transitions.create('padding-left', {
            easing: transitions.easing.sharp,
            duration: transitions.duration.enteringScreen
        })
    },
    active: {
        backgroundColor: palette.action.selected,
        borderLeft: `3px solid ${palette.action.active}`
    },
    nested: {
        paddingLeft: spacing(4)
    },
    minimized: {
        paddingLeft: spacing(2)
    },
    icon: {
        width: spacing(2),
        height: spacing(2),
        fontSize: typography.subtitle1.fontSize,
        margin: 0
    },
    iconActive: {
        marginLeft: -3 // to offset the border left on the active list item
    },
    text: {
        fontSize: '0.875rem' /* v3 body1 display size — theme.typography.body1.fontSize is 1rem for MUI internals */,
        color: palette.custom.darkishGrey,
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    },
    textActive: {
        color: palette.action.active,
        fontWeight: typography.fontWeightMedium
    }
});

const DrawerListItemConnected = withStyles(styles)(DrawerListItem);
export default DrawerListItemConnected;
